import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Box, Button, TextField, InputAdornment, Card, Typography,
  Chip, IconButton, Tooltip, CircularProgress, Checkbox, Collapse,
  Paper, Menu, MenuItem as MuiMenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface AdminOrder {
  id: number; shop_id: number; shop_name: string;
  items: { product_id: number; name: string; qty: number; unit?: string; price: number; subtotal: number }[];
  total: number; status: string; notes?: string; portal_note?: string;
  delivery_date?: string; route_name?: string; created_at: string; updated_at: string;
}

const STATUS_MAP: Record<string, { label: string; bg: string; color: string }> = {
  pending:    { label: "Submitted",    bg: "#FFF3E0", color: "#E65100" },
  confirmed:  { label: "Confirmed",    bg: "#E3F2FD", color: "#0D47A1" },
  processing: { label: "Under Review", bg: "#F3E5F5", color: "#6A1B9A" },
  delivered:  { label: "Delivered",    bg: "#E8F5E9", color: "#1B5E20" },
  cancelled:  { label: "Cancelled",   bg: "#FFEBEE", color: "#B71C1C" },
};

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Submitted" },
  { key: "processing", label: "Under Review" },
  { key: "confirmed", label: "Confirmed" },
  { key: "delivered", label: "Delivered" },
];

const DATE_FILTERS = [
  { key: "all", label: "All Dates" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "week", label: "This Week" },
];

const ASSIGN_FILTERS = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned" },
  { key: "unassigned", label: "Unassigned" },
];

type SortKey = "id" | "shop_name" | "created_at" | "delivery_date" | "total" | "status";

const ShopOrderList: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [allOrders, setAllOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [assignFilter, setAssignFilter] = useState("all");
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [bulkAnchor, setBulkAnchor] = useState<null | HTMLElement>(null);

  const token = localStorage.getItem("token") || "";
  const headers = { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` };

  const loadOrders = useCallback(async () => {
    try {
      const body: any = {};
      if (search.trim()) body.search = search.trim();
      const res = await fetch(`${API_URL}/shop-orders/all`, { method: "POST", headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) setAllOrders(data.data || []);
    } catch { showErrorMessage("Failed to load orders"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    setUpdatingIds(prev => new Set(prev).add(orderId));
    try {
      const res = await fetch(`${API_URL}/shop-orders/${orderId}/status`, { method: "PUT", headers, body: JSON.stringify({ status: newStatus }) });
      const data = await res.json();
      if (data.success) { showMessage(`Order #${orderId} ${newStatus}`); if (data.route_warning) showErrorMessage(data.route_warning); loadOrders(); }
      else showErrorMessage(data.message || "Failed");
    } catch { showErrorMessage("Network error"); }
    finally { setUpdatingIds(prev => { const n = new Set(prev); n.delete(orderId); return n; }); }
  };

  const handleBulkAction = async (action: string) => {
    setBulkAnchor(null);
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    for (const id of ids) { await handleStatusChange(id, action); }
    setSelected(new Set());
  };

  // Filtering
  const filteredOrders = useMemo(() => {
    let list = allOrders;
    if (statusFilter !== "all") list = list.filter(o => o.status === statusFilter);
    if (dateFilter === "today") {
      const today = dayjs().format("YYYY-MM-DD");
      list = list.filter(o => o.delivery_date === today);
    } else if (dateFilter === "tomorrow") {
      const tom = dayjs().add(1, "day").format("YYYY-MM-DD");
      list = list.filter(o => o.delivery_date === tom);
    } else if (dateFilter === "week") {
      const start = dayjs().startOf("week"); const end = dayjs().endOf("week");
      list = list.filter(o => o.delivery_date && dayjs(o.delivery_date).isBetween(start, end, "day", "[]"));
    }
    if (assignFilter === "assigned") list = list.filter(o => !!o.route_name);
    else if (assignFilter === "unassigned") list = list.filter(o => !o.route_name);
    return list;
  }, [allOrders, statusFilter, dateFilter, assignFilter]);

  // Sorting
  const sortedOrders = useMemo(() => {
    const sorted = [...filteredOrders].sort((a, b) => {
      let va: any, vb: any;
      switch (sortKey) {
        case "id": va = a.id; vb = b.id; break;
        case "shop_name": va = a.shop_name.toLowerCase(); vb = b.shop_name.toLowerCase(); break;
        case "created_at": va = a.created_at; vb = b.created_at; break;
        case "delivery_date": va = a.delivery_date || ""; vb = b.delivery_date || ""; break;
        case "total": va = a.total; vb = b.total; break;
        case "status": va = a.status; vb = b.status; break;
        default: va = a.id; vb = b.id;
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredOrders, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return null;
    return sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 12, ml: 0.25 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12, ml: 0.25 }} />;
  };

  // Counts
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: allOrders.length };
    allOrders.forEach(o => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, [allOrders]);

  const isOverdue = (o: AdminOrder) => o.status === "pending" && o.delivery_date && dayjs(o.delivery_date).isBefore(dayjs(), "day");
  const isUpdating = (id: number) => updatingIds.has(id);

  const toggleSelect = (id: number) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => {
    if (selected.size === sortedOrders.length) setSelected(new Set());
    else setSelected(new Set(sortedOrders.map(o => o.id)));
  };

  const getRowBg = (o: AdminOrder) => {
    if (isOverdue(o)) return "#FFF8F6";
    if (o.status === "pending") return "#FFFDF5";
    return undefined;
  };

  return (
    <Card className={classes.card}>
      <Grid container>
        {/* ── Header ── */}
        <Grid item xs={12} className={classes.titleGrid} sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
            <Typography className={classes.cardTitle}>Orders</Typography>
            <TextField size="small" placeholder="Search restaurant or order #..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: "#999" }} /></InputAdornment>, classes: { root: classes.formControlList } }}
              sx={{ width: 280, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Box>

          {/* ── Summary Bar ── */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            {[
              { icon: <InventoryIcon sx={{ fontSize: 16, color: "#1565C0" }} />, bg: "#E3F2FD", label: "Total Orders", value: allOrders.length },
              { icon: <PendingActionsIcon sx={{ fontSize: 16, color: "#E65100" }} />, bg: "#FFF3E0", label: "Pending", value: counts.pending || 0 },
              { icon: <DoneAllIcon sx={{ fontSize: 16, color: "#1B5E20" }} />, bg: "#E8F5E9", label: "Delivered", value: counts.delivered || 0 },
              { icon: <WarningAmberIcon sx={{ fontSize: 16, color: "#C62828" }} />, bg: "#FFEBEE", label: "Overdue", value: allOrders.filter(o => isOverdue(o)).length },
            ].map(s => (
              <Paper key={s.label} elevation={0} sx={{ px: 2, py: 1, borderRadius: 2, border: "1px solid #eee", display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</Box>
                <Box><Typography fontSize={10} color="#999" fontWeight={600} textTransform="uppercase">{s.label}</Typography><Typography fontSize={15} fontWeight={800}>{s.value}</Typography></Box>
              </Paper>
            ))}
          </Box>

          {/* ── Filters Row ── */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            {/* Status tabs */}
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {STATUS_TABS.map(t => {
                const count = t.key === "all" ? allOrders.length : (counts[t.key] || 0);
                const active = statusFilter === t.key;
                return (
                  <Chip key={t.key} label={`${t.label} (${count})`} onClick={() => setStatusFilter(t.key)} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 28,
                      ...(active ? { bgcolor: "#009688", color: "#fff" } : { bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: "#e0e0e0" } }) }} />
                );
              })}
            </Box>

            <Box sx={{ width: 1, height: 24, bgcolor: "#e0e0e0" }} />

            {/* Date filter */}
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {DATE_FILTERS.map(t => {
                const active = dateFilter === t.key;
                return (
                  <Chip key={t.key} label={t.label} onClick={() => setDateFilter(t.key)} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 28,
                      ...(active ? { bgcolor: "#1565C0", color: "#fff" } : { bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: "#e0e0e0" } }) }} />
                );
              })}
            </Box>

            <Box sx={{ width: 1, height: 24, bgcolor: "#e0e0e0" }} />

            {/* Assignment filter */}
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {ASSIGN_FILTERS.map(t => {
                const active = assignFilter === t.key;
                return (
                  <Chip key={t.key} label={t.label} onClick={() => setAssignFilter(t.key)} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 28,
                      ...(active ? { bgcolor: "#7B1FA2", color: "#fff" } : { bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: "#e0e0e0" } }) }} />
                );
              })}
            </Box>
          </Box>

          {/* ── Bulk Actions ── */}
          {selected.size > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, p: 1.5, bgcolor: "#E0F2F1", borderRadius: 2 }}>
              <Typography fontSize={13} fontWeight={600} color="#009688">{selected.size} selected</Typography>
              <Button size="small" variant="contained" onClick={(e) => setBulkAnchor(e.currentTarget)}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" } }}>
                Bulk Actions
              </Button>
              <Button size="small" variant="text" onClick={() => setSelected(new Set())}
                sx={{ textTransform: "none", fontSize: 12, color: "#999" }}>Clear</Button>
              <Menu anchorEl={bulkAnchor} open={!!bulkAnchor} onClose={() => setBulkAnchor(null)}>
                <MuiMenuItem onClick={() => handleBulkAction("confirmed")} sx={{ fontSize: 13 }}>
                  <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: "#2E7D32" }} /> Approve Selected
                </MuiMenuItem>
                <MuiMenuItem onClick={() => handleBulkAction("delivered")} sx={{ fontSize: 13 }}>
                  <LocalShippingIcon sx={{ fontSize: 16, mr: 1, color: "#1565C0" }} /> Mark Delivered
                </MuiMenuItem>
                <MuiMenuItem onClick={() => handleBulkAction("cancelled")} sx={{ fontSize: 13 }}>
                  <CancelIcon sx={{ fontSize: 16, mr: 1, color: "#C62828" }} /> Reject Selected
                </MuiMenuItem>
              </Menu>
            </Box>
          )}
        </Grid>

        {/* ── Table ── */}
        <Grid item xs={12}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={32} sx={{ color: "#009688" }} /></Box>
          ) : sortedOrders.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}><Typography fontSize={14} color="text.secondary">No orders found</Typography></Box>
          ) : (
            <TableContainer>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeaderRow}>
                  <TableRow>
                    <TableCell className={classes.tablheader} sx={{ width: 40, p: 1 }}>
                      <Checkbox size="small" checked={selected.size === sortedOrders.length && sortedOrders.length > 0}
                        indeterminate={selected.size > 0 && selected.size < sortedOrders.length}
                        onChange={selectAll} sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                    </TableCell>
                    {[
                      { key: "id" as SortKey, label: "Order #", w: undefined },
                      { key: "shop_name" as SortKey, label: "Restaurant", w: undefined },
                      { key: "created_at" as SortKey, label: "Date", w: undefined },
                      { key: "delivery_date" as SortKey, label: "Delivery", w: undefined },
                      { key: null, label: "Items", w: undefined },
                      { key: "total" as SortKey, label: "Total", w: undefined },
                      { key: "status" as SortKey, label: "Status", w: undefined },
                    ].map(col => (
                      <TableCell key={col.label} className={classes.tablheader}
                        onClick={() => col.key && toggleSort(col.key)}
                        sx={{ cursor: col.key ? "pointer" : "default", userSelect: "none", "&:hover": col.key ? { bgcolor: "rgba(0,0,0,0.04)" } : {} }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <strong>{col.label}</strong>
                          {col.key && <SortIcon k={col.key} />}
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
                    <TableCell className={classes.tablheader} sx={{ width: 36 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOrders.map((order) => {
                    const sm = STATUS_MAP[order.status] || { label: order.status, bg: "#f5f5f5", color: "#666" };
                    const overdue = isOverdue(order);
                    const expanded = expandedId === order.id;
                    return (
                      <React.Fragment key={order.id}>
                        <TableRow sx={{ bgcolor: getRowBg(order), "&:hover": { bgcolor: "#fafafa" }, transition: "background 0.1s" }}>
                          <TableCell className={classes.tablCell} sx={{ p: 1 }}>
                            <Checkbox size="small" checked={selected.has(order.id)} onChange={() => toggleSelect(order.id)}
                              sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={700}>#{order.id}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600}>{order.shop_name}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={12}>{dayjs(order.created_at).format("MMM D, YYYY")}</Typography>
                            <Typography fontSize={10} color="text.secondary">{dayjs(order.created_at).format("h:mm A")}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            {order.delivery_date ? (
                              <Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Typography fontSize={12} fontWeight={600}>
                                    {dayjs(order.delivery_date).format("ddd, MMM D")}
                                  </Typography>
                                  {overdue && <WarningAmberIcon sx={{ fontSize: 13, color: "#C62828" }} />}
                                </Box>
                                {order.route_name && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                    <AltRouteIcon sx={{ fontSize: 10, color: "#999" }} />
                                    <Typography fontSize={10} color="text.secondary">{order.route_name}</Typography>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Chip label="Unassigned" size="small" variant="outlined"
                                sx={{ fontSize: 10, height: 20, borderColor: "#e0e0e0", color: "#bbb" }} />
                            )}
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={12} fontWeight={600}>
                              {order.items.length} item{order.items.length !== 1 ? "s" : ""}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={700}>${order.total.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Chip label={sm.label} size="small"
                                sx={{ fontSize: 10, fontWeight: 700, bgcolor: sm.bg, color: sm.color, height: 22, borderRadius: "11px" }} />
                              {overdue && <Chip label="Overdue" size="small" sx={{ fontSize: 9, fontWeight: 700, bgcolor: "#FFEBEE", color: "#C62828", height: 18, borderRadius: "9px" }} />}
                            </Box>
                          </TableCell>
                          <TableCell className={classes.tablCell} sx={{ textAlign: "right" }}>
                            <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
                              <Tooltip title="View Order">
                                <IconButton size="small" onClick={() => navigate(`/shop-order/${order.id}`)}
                                  sx={{ color: "#999", "&:hover": { color: "#009688" } }}>
                                  <VisibilityIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                              {order.status === "pending" && (
                                <>
                                  <Tooltip title="Approve Order">
                                    <IconButton size="small" disabled={isUpdating(order.id)}
                                      onClick={() => handleStatusChange(order.id, "confirmed")}
                                      sx={{ color: "#999", "&:hover": { color: "#2E7D32" } }}>
                                      {isUpdating(order.id) ? <CircularProgress size={14} /> : <CheckCircleIcon sx={{ fontSize: 17 }} />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject Order">
                                    <IconButton size="small" disabled={isUpdating(order.id)}
                                      onClick={() => handleStatusChange(order.id, "cancelled")}
                                      sx={{ color: "#999", "&:hover": { color: "#C62828" } }}>
                                      <CancelIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title="Edit Pricing">
                                <IconButton size="small" onClick={() => navigate(`/shop-order/${order.id}`)}
                                  sx={{ color: "#999", "&:hover": { color: "#1565C0" } }}>
                                  <EditIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell className={classes.tablCell} sx={{ p: 0.5 }}>
                            <IconButton size="small" onClick={() => setExpandedId(expanded ? null : order.id)}
                              sx={{ color: "#ccc", "&:hover": { color: "#666" } }}>
                              {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        {/* ── Expanded Row ── */}
                        <TableRow>
                          <TableCell colSpan={11} sx={{ py: 0, px: 0, borderBottom: expanded ? "1px solid #e0e0e0" : "none" }}>
                            <Collapse in={expanded} timeout={200}>
                              <Box sx={{ px: 3, py: 2, bgcolor: "#FAFAFA" }}>
                                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                  <Box sx={{ flex: 1, minWidth: 300 }}>
                                    <Typography fontSize={11} fontWeight={700} color="#999" textTransform="uppercase" mb={1}>Order Items</Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8" }}>Product</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 60 }}>Qty</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 50 }}>Unit</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 70, textAlign: "right" }}>Price</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 80, textAlign: "right" }}>Subtotal</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {order.items.map((it, i) => (
                                          <TableRow key={i}>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5" }}>{it.name}</TableCell>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5" }}>{it.qty}</TableCell>
                                            <TableCell sx={{ fontSize: 11, py: 0.5, borderBottom: "1px solid #f5f5f5", color: "#999" }}>{it.unit || "—"}</TableCell>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>${it.price.toFixed(2)}</TableCell>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5", textAlign: "right", fontWeight: 600 }}>${it.subtotal.toFixed(2)}</TableCell>
                                          </TableRow>
                                        ))}
                                        <TableRow>
                                          <TableCell colSpan={4} sx={{ fontSize: 12, fontWeight: 700, py: 0.75, textAlign: "right", borderBottom: "none" }}>Total</TableCell>
                                          <TableCell sx={{ fontSize: 13, fontWeight: 800, py: 0.75, textAlign: "right", borderBottom: "none", color: "#009688" }}>${order.total.toFixed(2)}</TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </Box>
                                  <Box sx={{ minWidth: 200 }}>
                                    <Typography fontSize={11} fontWeight={700} color="#999" textTransform="uppercase" mb={1}>Details</Typography>
                                    {order.portal_note && (
                                      <Box sx={{ mb: 1, p: 1, bgcolor: "#E3F2FD", borderRadius: 1.5 }}>
                                        <Typography fontSize={10} fontWeight={600} color="#1565C0">Restaurant Note</Typography>
                                        <Typography fontSize={11} color="#333">{order.portal_note}</Typography>
                                      </Box>
                                    )}
                                    {order.notes && (
                                      <Box sx={{ mb: 1, p: 1, bgcolor: "#FFF3E0", borderRadius: 1.5 }}>
                                        <Typography fontSize={10} fontWeight={600} color="#E65100">Admin Note</Typography>
                                        <Typography fontSize={11} color="#333">{order.notes}</Typography>
                                      </Box>
                                    )}
                                    <Typography fontSize={11} color="#999">Created: {dayjs(order.created_at).format("MMM D, YYYY h:mm A")}</Typography>
                                    {order.updated_at !== order.created_at && (
                                      <Typography fontSize={11} color="#999">Updated: {dayjs(order.updated_at).format("MMM D, YYYY h:mm A")}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default ShopOrderList;
